import React, { useState, useEffect } from 'react';
import {
  Video, Star, Calendar, Clock, Search, ExternalLink,
  Sparkles, BookOpen, Check, X, ShieldCheck, FileText, Edit3, Send
} from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../context/AlertContext';

const SUBJECT_CATEGORIES = [
  'All Fields',
  'AI & Data Science',
  'Computer Science',
  'Mathematics',
  'Physics',
  'Chemistry',
  'Competitive Exams',
  'Scholarship Guidance'
];

const LEVEL_OPTIONS = [
  'All Levels',
  'Class 11-12',
  'Undergraduate',
  'B.Tech / UG',
  'Intermediate',
  'Advanced'
];

/* ──────────────────────────────────────────
   BOOKING CONFIRMATION MODAL
   ────────────────────────────────────────── */
function BookingConfirmationModal({ session, onConfirm, onClose, loading }) {
  const dateObj = new Date(session.date);
  const formattedDate = dateObj.toLocaleDateString('en-IN', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        overflowY: 'auto',
      }}
    >
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.7)',
          backdropFilter: 'blur(6px)',
        }}
        onClick={!loading ? onClose : undefined}
      />

      {/* Card */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: '520px',
          width: '92vw',
          borderRadius: '1.75rem',
          background: '#ffffff',
          boxShadow: '0 25px 60px -12px rgba(0,0,0,0.35)',
          overflow: 'hidden',
          animation: 'scaleIn 0.2s cubic-bezier(0.25, 1, 0.5, 1) both',
          border: '1px solid rgba(147, 51, 234, 0.15)',
        }}
      >
        {/* Top Header */}
        <div
          style={{
            padding: '1.75rem 2rem 1.25rem',
            background: 'linear-gradient(135deg, #faf5ff 0%, #ede9fe 100%)',
            borderBottom: '1px solid rgba(147,51,234,0.12)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '3px 10px',
                borderRadius: 20,
                background: 'rgba(147,51,234,0.1)',
                border: '1px solid rgba(147,51,234,0.2)',
                color: 'var(--primary-gold)',
                fontSize: '0.72rem',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: 6,
              }}
            >
              <Sparkles size={12} /> Confirm Your Attendance
            </div>
            <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 800, color: 'var(--navy-dark)' }}>
              Reserve Mentoring Seat
            </h3>
          </div>

          <button
            onClick={!loading ? onClose : undefined}
            disabled={loading}
            style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              width: 34,
              height: 34,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body Overview Card */}
        <div style={{ padding: '1.5rem 2rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Mentor Profile Preview */}
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              alignItems: 'center',
              padding: '1rem',
              background: '#f8fafc',
              borderRadius: '1.25rem',
              border: '1px solid #e2e8f0',
            }}
          >
            <img
              src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${session.mentor_name || session.id}`}
              alt={session.mentor_name}
              style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                border: '2px solid #8b5cf6',
                background: '#fff',
                flexShrink: 0,
              }}
            />
            <div style={{ minWidth: 0, flex: 1 }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--primary-gold)', textTransform: 'uppercase' }}>
                {session.subject}
              </span>
              <h4 style={{ margin: '0.15rem 0', fontSize: '1rem', fontWeight: 800, color: 'var(--navy-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {session.mentor_name}
              </h4>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#475569', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {session.title}
              </p>
            </div>
          </div>

          {/* Key Session Details Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div style={{ padding: '0.75rem 1rem', background: '#f8fafc', borderRadius: '0.85rem', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#64748b', fontSize: '0.72rem', fontWeight: 700 }}>
                <Calendar size={13} color="#8b5cf6" /> Scheduled Date
              </div>
              <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--navy-dark)', marginTop: 3 }}>
                {formattedDate}
              </div>
            </div>

            <div style={{ padding: '0.75rem 1rem', background: '#f8fafc', borderRadius: '0.85rem', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#64748b', fontSize: '0.72rem', fontWeight: 700 }}>
                <Clock size={13} color="#2563eb" /> Time & Duration
              </div>
              <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--navy-dark)', marginTop: 3 }}>
                {session.time || '17:00 IST'} ({session.duration || '90 min'})
              </div>
            </div>
          </div>

          {/* Checklist what happens */}
          <div style={{ background: 'rgba(147, 51, 234, 0.04)', border: '1px solid rgba(147, 51, 234, 0.12)', borderRadius: '1rem', padding: '0.85rem 1.15rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--navy-dark)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              What you get upon confirmation:
            </span>
            <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.2rem', fontSize: '0.8rem', color: '#475569', lineHeight: 1.5 }}>
              <li>Instant Google Meet / Teams live room access link.</li>
              <li>Adds automatically to your Dashboard <strong>Upcoming Events</strong>.</li>
              <li>Increases your <strong>Video Learning</strong> track and maintains your study streak.</li>
            </ul>
          </div>
        </div>

        {/* Footer actions */}
        <div
          style={{
            padding: '1.25rem 2rem',
            background: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '0.75rem',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '0.7rem 1.25rem',
              background: '#ffffff',
              border: '1.5px solid #cbd5e1',
              borderRadius: '0.85rem',
              fontSize: '0.85rem',
              fontWeight: 700,
              color: '#475569',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.7rem 1.6rem',
              background: 'var(--primary-gold)',
              border: 'none',
              borderRadius: '0.85rem',
              fontSize: '0.88rem',
              fontWeight: 800,
              color: '#ffffff',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              boxShadow: '0 4px 12px var(--gold-shadow)',
            }}
          >
            {loading ? (
              'Reserving Seat...'
            ) : (
              <>
                <Check size={16} /> Confirm Booking (Free)
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────
   RATE MENTOR MODAL
   ────────────────────────────────────────── */
function RateMentorModal({ session, onClose, onSubmitted }) {
  const [rating, setRating] = useState(5);
  const [hovered, setHovered] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { showAlert } = useAlert();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.rateMentorSession(session.id || session._id, rating, comment);
      showAlert('Feedback Submitted', `Thank you for rating ${session.mentor_name}!`);
      onSubmitted();
      onClose();
    } catch (err) {
      showAlert('Rating Error', err?.message || 'Failed to submit rating. Make sure you are enrolled.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', overflowY: 'auto' }}>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(6px)' }} onClick={onClose} />
      <div style={{ position: 'relative', zIndex: 10, maxWidth: '480px', width: '92vw', borderRadius: '1.5rem', background: '#ffffff', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', overflow: 'hidden', animation: 'scaleIn 0.2s ease-out' }}>
        <div style={{ padding: '1.5rem 1.75rem', background: 'linear-gradient(135deg, #faf5ff 0%, #ede9fe 100%)', borderBottom: '1px solid rgba(147,51,234,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--primary-gold)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Session Review</span>
            <h3 style={{ margin: '0.2rem 0 0', fontSize: '1.2rem', fontWeight: 800, color: 'var(--navy-dark)' }}>Rate {session.mentor_name}</h3>
          </div>
          <button onClick={onClose} style={{ background: '#fff', border: '1px solid #e2e8f0', width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', cursor: 'pointer' }}><X size={15} /></button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '1.5rem 1.75rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#334155', marginBottom: '0.5rem' }}>Your Rating</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  type="button" key={star}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHovered(star)}
                  onMouseLeave={() => setHovered(rating)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, transition: 'transform 0.15s' }}
                >
                  <Star size={28} color={star <= hovered ? '#f59e0b' : '#cbd5e1'} fill={star <= hovered ? '#f59e0b' : 'none'} />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#334155', marginBottom: '0.4rem' }}>Feedback & Takeaways (Optional)</label>
            <textarea
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What did you learn? How was the mentor's teaching style?"
              style={{ width: '100%', padding: '0.75rem', borderRadius: '0.75rem', border: '1.5px solid #e2e8f0', fontSize: '0.85rem', fontFamily: 'inherit', resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <button type="button" onClick={onClose} style={{ padding: '0.6rem 1.25rem', background: '#f1f5f9', border: 'none', borderRadius: '0.75rem', fontWeight: 700, fontSize: '0.85rem', color: '#475569', cursor: 'pointer' }}>Cancel</button>
            <button type="submit" disabled={submitting} style={{ padding: '0.6rem 1.5rem', background: 'var(--primary-gold)', border: 'none', borderRadius: '0.75rem', fontWeight: 800, fontSize: '0.85rem', color: '#fff', cursor: 'pointer', boxShadow: '0 2px 8px rgba(147,51,234,0.2)' }}>
              {submitting ? 'Submitting...' : 'Submit Review'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────
   SHARE / EDIT SESSION NOTES MODAL (mentor only)
   ────────────────────────────────────────── */
function ShareNotesModal({ session, onClose, onSaved }) {
  const [notes, setNotes] = useState(session.sessionNotes?.content || '');
  const [saving, setSaving] = useState(false);
  const { showAlert } = useAlert();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!notes.trim()) { showAlert('Empty Notes', 'Please write some notes before sharing.'); return; }
    setSaving(true);
    try {
      await api.shareSessionNotes(session.id || session._id, notes);
      showAlert('Notes Shared! 📋', 'Your session notes are now visible to all enrolled students.');
      onSaved();
      onClose();
    } catch (err) {
      showAlert('Error', err?.message || 'Failed to share notes.');
    } finally {
      setSaving(false);
    }
  };

  const hasExisting = !!(session.sessionNotes?.content);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', overflowY: 'auto' }}>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.7)', backdropFilter: 'blur(6px)' }} onClick={!saving ? onClose : undefined} />
      <div style={{ position: 'relative', zIndex: 10, maxWidth: '560px', width: '92vw', borderRadius: '1.75rem', background: '#ffffff', boxShadow: '0 25px 60px -12px rgba(0,0,0,0.35)', overflow: 'hidden', animation: 'scaleIn 0.2s cubic-bezier(0.25,1,0.5,1) both' }}>

        {/* Header */}
        <div style={{ padding: '1.75rem 2rem 1.25rem', background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 100%)', borderBottom: '1px solid rgba(245,158,11,0.18)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.25)', color: '#b45309', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
              <FileText size={12} /> {hasExisting ? 'Edit Session Notes' : 'Share Session Notes'}
            </div>
            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, color: 'var(--navy-dark)' }}>
              {session.title}
            </h3>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: '#64748b' }}>Notes will be visible to all enrolled students</p>
          </div>
          <button onClick={!saving ? onClose : undefined} disabled={saving} style={{ background: '#fff', border: '1px solid #e2e8f0', width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', cursor: saving ? 'not-allowed' : 'pointer' }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} style={{ padding: '1.5rem 2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#334155', marginBottom: '0.5rem' }}>
              Session Notes &amp; Key Takeaways
            </label>
            <textarea
              rows={8}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={'Write your session notes here...\n\n• Key topics covered\n• Important formulas / concepts\n• Resources & further reading\n• Homework / action items for students'}
              style={{ width: '100%', padding: '0.85rem', borderRadius: '0.85rem', border: '1.5px solid #e2e8f0', fontSize: '0.85rem', fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.6, outline: 'none', boxSizing: 'border-box' }}
            />
            <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 4 }}>{notes.length} characters</div>
          </div>

          {hasExisting && (
            <div style={{ padding: '0.6rem 0.85rem', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '0.65rem', fontSize: '0.78rem', color: '#92400e' }}>
              <Edit3 size={12} style={{ marginRight: 5, verticalAlign: 'middle' }} />
              Last updated: {new Date(session.sessionNotes.updatedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} disabled={saving} style={{ padding: '0.7rem 1.25rem', background: '#f1f5f9', border: 'none', borderRadius: '0.85rem', fontWeight: 700, fontSize: '0.85rem', color: '#475569', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.7rem 1.5rem', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: '0.85rem', fontWeight: 800, fontSize: '0.88rem', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 12px rgba(245,158,11,0.3)' }}>
              <Send size={15} /> {saving ? 'Sharing...' : hasExisting ? 'Update Notes' : 'Share Notes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function SessionDetailsModal({ session, isBooked, isEnrolled, isMentorHost, onRequestBook, onRate, onShareNotes, onClose }) {
  const isFull = session.enrolled_count >= session.total_capacity;
  const meetUrl = session.meetingLink || 'https://meet.google.com/new';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', overflowY: 'auto' }}>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(6px)' }} onClick={onClose} />
      <div style={{ position: 'relative', zIndex: 10, maxWidth: '640px', width: '92vw', maxHeight: '88vh', overflowY: 'auto', borderRadius: '1.75rem', background: '#ffffff', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column' }}>
        
        {/* Header */}
        <div style={{ padding: '1.75rem 2rem', background: 'linear-gradient(135deg, #faf5ff 0%, #ede9fe 100%)', borderBottom: '1px solid rgba(147,51,234,0.12)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${session.mentor_name || session.id}`} alt={session.mentor_name} style={{ width: 56, height: 56, borderRadius: '50%', border: '2.5px solid #8b5cf6', background: '#fff' }} />
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--primary-gold)', textTransform: 'uppercase' }}>{session.subject}</span>
                <span style={{ fontSize: '0.7rem', padding: '1px 6px', background: 'rgba(16,185,129,0.12)', color: '#059669', borderRadius: 6, fontWeight: 700 }}>Verified Mentor</span>
              </div>
              <h3 style={{ margin: '0.2rem 0', fontSize: '1.25rem', fontWeight: 800, color: 'var(--navy-dark)' }}>{session.mentor_name}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                {[1,2,3,4,5].map(i => <Star key={i} size={11} color="#f59e0b" fill="#f59e0b" />)}
                <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700, marginLeft: 4 }}>5.0 Rating (40+ sessions hosted)</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: '#fff', border: '1px solid #e2e8f0', width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', cursor: 'pointer' }}><X size={16} /></button>
        </div>

        {/* Content */}
        <div style={{ padding: '1.75rem 2rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div>
            <h4 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--navy-dark)', marginBottom: '0.4rem' }}>{session.title}</h4>
            <p style={{ fontSize: '0.85rem', color: '#475569', lineHeight: 1.6, margin: 0 }}>{session.description}</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
            <div style={{ padding: '0.75rem 1rem', background: '#f8fafc', borderRadius: '0.85rem', border: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Date & Time</span>
              <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--navy-dark)', marginTop: 2 }}>{new Date(session.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })} · {session.time || '17:00 IST'}</div>
            </div>
            <div style={{ padding: '0.75rem 1rem', background: '#f8fafc', borderRadius: '0.85rem', border: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Duration</span>
              <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--navy-dark)', marginTop: 2 }}>{session.duration || '90 min'}</div>
            </div>
            <div style={{ padding: '0.75rem 1rem', background: '#f8fafc', borderRadius: '0.85rem', border: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Target Level</span>
              <div style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--navy-dark)', marginTop: 2 }}>{session.level || 'All Levels'}</div>
            </div>
            <div style={{ padding: '0.75rem 1rem', background: '#f8fafc', borderRadius: '0.85rem', border: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Seats Booked</span>
              <div style={{ fontSize: '0.85rem', fontWeight: 800, color: isFull ? '#ef4444' : '#10b981', marginTop: 2 }}>{session.enrolled_count || 0} / {session.total_capacity || 25}</div>
            </div>
          </div>

          {/* Post-Booking Live Class Link if booked */}
          {isBooked ? (
            <div style={{ padding: '1.25rem', background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', border: '1.5px solid #a7f3d0', borderRadius: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#065f46', fontWeight: 800, fontSize: '0.9rem', marginBottom: '0.35rem' }}>
                <ShieldCheck size={18} color="#059669" /> You are enrolled in this live masterclass!
              </div>
              <p style={{ fontSize: '0.8rem', color: '#047857', margin: '0 0 0.85rem' }}>
                Click below to join the live video session via Google Meet. The room opens 10 mins prior to the start time.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <a
                  href={meetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 1.25rem', background: '#059669', color: '#fff', borderRadius: '0.75rem', fontWeight: 800, fontSize: '0.85rem', textDecoration: 'none', boxShadow: '0 2px 8px rgba(5,150,105,0.25)' }}
                >
                  <Video size={16} /> Join Google Meet <ExternalLink size={13} />
                </a>
                <button
                  onClick={onRate}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.65rem 1.25rem', background: '#ffffff', color: '#065f46', border: '1.5px solid #6ee7b7', borderRadius: '0.75rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  <Star size={15} color="#f59e0b" fill="#f59e0b" /> Rate Session
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => {
                onClose();
                onRequestBook(session);
              }}
              disabled={isFull}
              style={{ padding: '0.85rem', background: isFull ? '#cbd5e1' : 'var(--primary-gold)', color: '#fff', border: 'none', borderRadius: '0.85rem', fontWeight: 800, fontSize: '0.95rem', cursor: isFull ? 'not-allowed' : 'pointer', fontFamily: 'inherit', boxShadow: isFull ? 'none' : '0 4px 12px var(--gold-shadow)' }}
            >
              {isFull ? 'Session is Full' : 'Book Live Session (Free)'}
            </button>
          )}

          {/* Mentor: Share / Edit Notes button */}
          {isMentorHost && session.status === 'ended' && (
            <button
              onClick={onShareNotes}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.25rem', background: 'linear-gradient(135deg, #fef3c7, #fde68a)', border: '1.5px solid #fbbf24', borderRadius: '0.85rem', fontWeight: 800, fontSize: '0.88rem', color: '#92400e', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              <FileText size={16} /> {session.sessionNotes?.content ? 'Edit Session Notes' : 'Share Session Notes'}
            </button>
          )}

          {/* Enrolled students: view session notes */}
          {isEnrolled && session.sessionNotes?.content && (
            <div style={{ padding: '1.25rem 1.5rem', background: 'linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)', border: '1.5px solid rgba(245,158,11,0.3)', borderRadius: '1.1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.65rem' }}>
                <FileText size={16} color="#d97706" />
                <span style={{ fontWeight: 800, fontSize: '0.88rem', color: '#92400e' }}>Session Notes</span>
                {session.sessionNotes.updatedAt && (
                  <span style={{ fontSize: '0.7rem', color: '#b45309', marginLeft: 'auto', fontWeight: 600 }}>
                    Updated {new Date(session.sessionNotes.updatedAt).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
                  </span>
                )}
              </div>
              <pre style={{ margin: 0, fontSize: '0.83rem', color: '#44403c', lineHeight: 1.65, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                {session.sessionNotes.content}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────
   MENTOR CARD
   ────────────────────────────────────────── */
function MentorCard({ session, isBooked, isEnrolled, isMentorHost, onRequestBook, onOpenDetails, onRate, onShareNotes }) {
  const isFull = session.enrolled_count >= session.total_capacity;
  const dateObj = new Date(session.date);
  const formattedDate = dateObj.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', weekday: 'short' });
  const meetUrl = session.meetingLink || 'https://meet.google.com/new';

  const occupancyPct = Math.min(100, Math.round(((session.enrolled_count || 0) / (session.total_capacity || 25)) * 100));

  return (
    <div
      onClick={() => onOpenDetails(session)}
      className="glass-card"
      style={{
        display: 'flex', flexDirection: 'column', gap: '0.85rem',
        borderRadius: '1.25rem', background: '#ffffff',
        border: isBooked ? '2px solid #10b981' : '1px solid rgba(0,0,0,0.06)',
        padding: '1.4rem', cursor: 'pointer', transition: 'all 0.25s cubic-bezier(.22,1,.36,1)',
        position: 'relative', overflow: 'hidden',
        boxShadow: isBooked ? '0 8px 24px rgba(16,185,129,0.1)' : '0 4px 16px rgba(0,0,0,0.03)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: isBooked ? '#10b981' : 'linear-gradient(90deg,var(--primary-gold),#8b5cf6)' }} />

      {/* Top Mentor Avatar & Subject */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <img
            src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${session.mentor_name || session.id}`}
            alt={session.mentor_name}
            style={{ width: 44, height: 44, borderRadius: '50%', border: '2px solid rgba(147,51,234,0.15)', background: 'rgba(147,51,234,0.05)' }}
          />
          <div>
            <h4 style={{ color: 'var(--navy-dark)', fontWeight: 800, fontSize: '0.92rem', margin: 0 }}>{session.mentor_name || 'Expert Mentor'}</h4>
            <span style={{ color: 'var(--primary-gold)', fontSize: '0.72rem', fontWeight: 700 }}>{session.subject || 'General'}</span>
          </div>
        </div>

        {isBooked ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: 'rgba(16,185,129,0.12)', color: '#059669', border: '1px solid rgba(16,185,129,0.3)', fontSize: '0.7rem', fontWeight: 800, padding: '2px 8px', borderRadius: 20 }}>
            <Check size={12} /> Enrolled
          </span>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
            <Star size={11} color="#f59e0b" fill="#f59e0b" />
            <span style={{ color: '#475569', fontSize: '0.72rem', fontWeight: 800 }}>5.0</span>
          </div>
        )}
      </div>

      {/* Title & Description */}
      <div>
        <h5 style={{ color: 'var(--navy-dark)', fontWeight: 800, fontSize: '0.95rem', margin: '0 0 0.25rem', lineHeight: 1.35 }}>
          {session.title || 'Mentoring Session'}
        </h5>
        <p style={{ color: '#64748b', fontSize: '0.78rem', lineHeight: 1.5, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {session.description}
        </p>
      </div>

      {/* Badges: Date, Time, Level */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.7rem', padding: '0.2rem 0.6rem', background: '#f8fafc', color: '#334155', borderRadius: '0.5rem', border: '1px solid #e2e8f0', fontWeight: 700 }}>
          <Calendar size={11} color="#8b5cf6" /> {formattedDate} · {session.time || '17:00 IST'}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.7rem', padding: '0.2rem 0.6rem', background: '#f8fafc', color: '#334155', borderRadius: '0.5rem', border: '1px solid #e2e8f0', fontWeight: 700 }}>
          <Clock size={11} color="#2563eb" /> {session.duration || '90 min'}
        </span>
        <span style={{ fontSize: '0.7rem', padding: '0.2rem 0.6rem', background: 'rgba(147, 51, 234, 0.06)', color: 'var(--primary-gold)', borderRadius: '0.5rem', border: '1px solid rgba(147, 51, 234, 0.15)', fontWeight: 700 }}>
          {session.level || 'All Levels'}
        </span>
      </div>

      {/* Capacity Bar */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#64748b', fontWeight: 700, marginBottom: 3 }}>
          <span>Seats Available</span>
          <span style={{ color: isFull ? '#ef4444' : '#10b981' }}>{session.enrolled_count || 0} / {session.total_capacity || 25} filled</span>
        </div>
        <div style={{ height: 4, background: '#f1f5f9', borderRadius: 9999, overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 9999, width: `${occupancyPct}%`, background: isFull ? '#ef4444' : 'linear-gradient(90deg, #10b981, #3b82f6)' }} />
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem', flexDirection: 'column' }}>
        {/* Notes Available badge for enrolled students */}
        {isEnrolled && session.sessionNotes?.content && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.75rem', background: 'linear-gradient(135deg,#fefce8,#fef9c3)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '0.6rem', fontSize: '0.72rem', fontWeight: 700, color: '#92400e' }}>
            <FileText size={12} color="#d97706" /> Session Notes Available
          </div>
        )}

        {/* Mentor: Share / Edit Notes button */}
        {isMentorHost && session.status === 'ended' && (
          <button
            onClick={(e) => { e.stopPropagation(); onShareNotes(session); }}
            style={{ width: '100%', padding: '0.6rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', background: 'linear-gradient(135deg,#fef3c7,#fde68a)', border: '1.5px solid #fbbf24', borderRadius: '0.75rem', fontWeight: 800, fontSize: '0.78rem', color: '#92400e', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            <FileText size={13} /> {session.sessionNotes?.content ? 'Edit Notes' : 'Share Session Notes'}
          </button>
        )}

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {isBooked ? (
            <>
              <a
                href={meetUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ flex: 2, padding: '0.6rem', background: '#059669', color: '#fff', borderRadius: '0.75rem', fontWeight: 800, fontSize: '0.78rem', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem', boxShadow: '0 2px 6px rgba(5,150,105,0.2)' }}
              >
                <Video size={13} /> Join Live Meet
              </a>
              <button
                onClick={(e) => { e.stopPropagation(); onRate(session); }}
                style={{ flex: 1, padding: '0.6rem', background: '#f8fafc', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '0.75rem', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}
              >
                <Star size={12} color="#f59e0b" fill="#f59e0b" /> Rate
              </button>
            </>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRequestBook(session);
              }}
              disabled={isFull}
              style={{
                width: '100%', padding: '0.65rem', borderRadius: '0.75rem', fontWeight: 800, fontSize: '0.8rem', cursor: isFull ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
                background: isFull ? '#f1f5f9' : 'var(--primary-gold)',
                color: isFull ? '#94a3b8' : '#ffffff',
                border: 'none',
                boxShadow: isFull ? 'none' : '0 2px 8px var(--gold-shadow)',
              }}
            >
              {isFull ? 'Session Full' : 'Book Session (Free)'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────
   MAIN MENTORS PAGE
   ────────────────────────────────────────── */
export default function Mentors() {
  const [sessions, setSessions] = useState([]);
  const [bookedIds, setBookedIds] = useState(new Set());
  const [enrolledIds, setEnrolledIds] = useState(new Set()); // sessions user is enrolled in
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('All Fields');
  const [selectedLevel, setSelectedLevel] = useState('All Levels');
  const [onlyBooked, setOnlyBooked] = useState(false);

  const [activeModalSession, setActiveModalSession] = useState(null);
  const [confirmingSession, setConfirmingSession] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [ratingSession, setRatingSession] = useState(null);
  const [sharingNotesSession, setSharingNotesSession] = useState(null);

  const { user } = useAuth();
  const { showAlert } = useAlert();

  const checkIsMentorHost = (session) => {
    if (!user || !session) return false;
    if (user.role === 'admin') return true;
    const uid = String(user.id || user._id || '');
    const mid = session.mentor_id
      ? typeof session.mentor_id === 'object'
        ? String(session.mentor_id.id || session.mentor_id._id || '')
        : String(session.mentor_id)
      : '';
    return Boolean(uid && mid && uid === mid);
  };

  const fetchSessions = async () => {
    try {
      const data = await api.getCollection('mentoring_sessions');
      setSessions(data || []);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrolledIds = async () => {
    if (!user) return;
    try {
      const ids = await api.getMyEnrolledSessionIds();
      setEnrolledIds(new Set(ids || []));
      setBookedIds(new Set(ids || [])); // sync booked state too
    } catch {
      // non-critical — fall back to empty
    }
  };

  useEffect(() => {
    fetchSessions();
    fetchEnrolledIds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleRequestBook = (session) => {
    if (!user) { showAlert('Login Required', 'Please login to book a mentoring session.'); return; }
    setConfirmingSession(session);
  };

  const handleConfirmBooking = async () => {
    if (!confirmingSession) return;
    setBookingLoading(true);
    try {
      await api.bookSession(confirmingSession.id || confirmingSession._id);
      showAlert('Booking Confirmed! 🚀', `You are enrolled in "${confirmingSession.title}" with ${confirmingSession.mentor_name}.`);
      setBookedIds(prev => new Set([...prev, confirmingSession.id || confirmingSession._id]));
      setConfirmingSession(null);
      fetchSessions();
    } catch (e) {
      const message = e?.message?.includes('full') ? 'This session has reached maximum capacity.'
        : e?.message?.includes('Already booked') ? 'You have already booked this session.'
        : 'Failed to book session. You may already be registered.';
      showAlert('Booking Status', message);
      if (e?.message?.includes('Already booked')) {
        setBookedIds(prev => new Set([...prev, confirmingSession.id || confirmingSession._id]));
      }
      setConfirmingSession(null);
    } finally {
      setBookingLoading(false);
    }
  };

  const filteredSessions = sessions.filter(s => {
    const sId = s.id || s._id;
    if (onlyBooked && !bookedIds.has(sId)) return false;

    if (selectedSubject !== 'All Fields') {
      const subjMatch = (s.subject || '').toLowerCase().includes(selectedSubject.toLowerCase())
        || selectedSubject.toLowerCase().includes((s.subject || '').toLowerCase());
      if (!subjMatch) return false;
    }

    if (selectedLevel !== 'All Levels') {
      const lvlMatch = (s.level || '').toLowerCase().includes(selectedLevel.toLowerCase());
      if (!lvlMatch) return false;
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      const match = (s.title || '').toLowerCase().includes(term)
        || (s.mentor_name || '').toLowerCase().includes(term)
        || (s.description || '').toLowerCase().includes(term)
        || (s.subject || '').toLowerCase().includes(term);
      if (!match) return false;
    }

    return true;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
      
      {/* Header Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #f5f3ff 0%, #e0e7ff 100%)',
        borderRadius: '1.5rem', padding: '2rem 2.25rem',
        position: 'relative', overflow: 'hidden',
        border: '1px solid rgba(147,51,234,0.12)',
        boxShadow: '0 10px 30px rgba(147,51,234,0.04)'
      }}>
        <div style={{ position: 'absolute', right: -20, top: -20, width: 200, height: 200, background: 'radial-gradient(circle, rgba(147,51,234,0.12), transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(147,51,234,0.08)', border: '1px solid rgba(147,51,234,0.18)', borderRadius: 20, padding: '4px 12px', fontSize: 10, fontWeight: 800, color: 'var(--primary-gold)', textTransform: 'uppercase', letterSpacing: '.12em', marginBottom: 10 }}>
            <Sparkles size={13} /> Live Interactive Masterclasses
          </div>
          <h2 style={{ fontSize: '2.1rem', fontWeight: 900, color: 'var(--navy-dark)', margin: '0 0 0.4rem', letterSpacing: '-0.02em' }}>
            Mentoring Hub & Live Classes
          </h2>
          <p style={{ fontSize: '0.95rem', color: '#64748b', margin: 0, maxWidth: '650px', lineHeight: 1.5 }}>
            Book interactive 1-on-1 and small-group live video sessions with verified professors, FAANG engineers, and research scholars.
          </p>
        </div>
      </div>

      {/* Filter Toolbar & Search */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', background: '#ffffff', padding: '1.25rem', borderRadius: '1.25rem', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 4px 14px rgba(0,0,0,0.02)' }}>
        
        {/* Search Bar + Toggle */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 260 }}>
            <Search size={16} color="#94a3b8" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by topic, mentor name, or keywords (e.g. AI, Calculus, JEE, FAANG)..."
              style={{ width: '100%', padding: '0.7rem 1rem 0.7rem 2.5rem', borderRadius: '0.75rem', border: '1.5px solid #e2e8f0', fontSize: '0.85rem', outline: 'none', fontFamily: 'inherit' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              style={{ padding: '0.65rem 1rem', borderRadius: '0.75rem', border: '1.5px solid #e2e8f0', fontSize: '0.82rem', fontWeight: 700, color: 'var(--navy-dark)', background: '#ffffff', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              {LEVEL_OPTIONS.map(lvl => <option key={lvl} value={lvl}>{lvl}</option>)}
            </select>

            <button
              onClick={() => setOnlyBooked(prev => !prev)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.65rem 1.15rem',
                borderRadius: '0.75rem', fontSize: '0.82rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
                background: onlyBooked ? 'var(--primary-gold)' : '#f8fafc',
                color: onlyBooked ? '#ffffff' : '#475569',
                border: onlyBooked ? 'none' : '1.5px solid #e2e8f0',
                transition: 'all 0.15s'
              }}
            >
              <Check size={14} /> My Booked Sessions ({bookedIds.size})
            </button>
          </div>
        </div>

        {/* Subject Category Chips */}
        <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: 4 }}>
          {SUBJECT_CATEGORIES.map(cat => {
            const active = selectedSubject === cat;
            return (
              <button
                key={cat}
                onClick={() => setSelectedSubject(cat)}
                style={{
                  padding: '0.45rem 1rem', borderRadius: 20, fontSize: '0.78rem', fontWeight: active ? 800 : 600,
                  background: active ? 'var(--primary-gold)' : '#f8fafc',
                  color: active ? '#ffffff' : '#64748b',
                  border: active ? 'none' : '1px solid #e2e8f0',
                  cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s', fontFamily: 'inherit'
                }}
              >
                {cat}
              </button>
            );
          })}
        </div>
      </div>

      {/* Grid of Sessions */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '5rem', color: '#64748b' }}>
          <Video size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
          <p>Syncing active mentor masterclasses...</p>
        </div>
      ) : filteredSessions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '5rem 2rem', background: '#ffffff', borderRadius: '1.25rem', border: '1.5px dashed #cbd5e1' }}>
          <BookOpen size={44} color="#94a3b8" style={{ margin: '0 auto 0.75rem' }} />
          <h4 style={{ color: 'var(--navy-dark)', fontWeight: 800, marginBottom: '0.35rem' }}>No mentoring sessions found</h4>
          <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0 }}>Try clearing filters or search terms.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.25rem' }}>
          {filteredSessions.map(session => {
            const sId = session.id || session._id;
            const isBooked = bookedIds.has(sId);
            const isEnrolled = enrolledIds.has(sId);
            const isMentorHost = checkIsMentorHost(session);
            return (
              <MentorCard
                key={sId}
                session={session}
                isBooked={isBooked}
                isEnrolled={isEnrolled}
                isMentorHost={isMentorHost}
                onRequestBook={() => handleRequestBook(session)}
                onOpenDetails={() => setActiveModalSession(session)}
                onRate={() => setRatingSession(session)}
                onShareNotes={(s) => setSharingNotesSession(s)}
              />
            );
          })}
        </div>
      )}

      {/* Modals */}
      {confirmingSession && (
        <BookingConfirmationModal
          session={confirmingSession}
          loading={bookingLoading}
          onConfirm={handleConfirmBooking}
          onClose={() => setConfirmingSession(null)}
        />
      )}

      {activeModalSession && (() => {
        const sId = activeModalSession.id || activeModalSession._id;
        const isEnrolled = enrolledIds.has(sId);
        const isMentorHost = checkIsMentorHost(activeModalSession);
        return (
          <SessionDetailsModal
            session={activeModalSession}
            isBooked={bookedIds.has(sId)}
            isEnrolled={isEnrolled}
            isMentorHost={isMentorHost}
            onRequestBook={handleRequestBook}
            onShareNotes={() => {
              const s = activeModalSession;
              setActiveModalSession(null);
              setSharingNotesSession(s);
            }}
            onRate={() => {
              const s = activeModalSession;
              setActiveModalSession(null);
              setRatingSession(s);
            }}
            onClose={() => setActiveModalSession(null)}
          />
        );
      })()}

      {ratingSession && (
        <RateMentorModal
          session={ratingSession}
          onClose={() => setRatingSession(null)}
          onSubmitted={fetchSessions}
        />
      )}

      {sharingNotesSession && (
        <ShareNotesModal
          session={sharingNotesSession}
          onClose={() => setSharingNotesSession(null)}
          onSaved={() => { fetchSessions(); setSharingNotesSession(null); }}
        />
      )}
    </div>
  );
}
